import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import { RootState } from "../../../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import SurveyQuestionForm from "./SurveyQuestionForm";
import { useState } from "react";
import { addQuestion, copyQuestion, removeQuestion } from "../../../../../../redux/surveySlice";
import "./SurveyQuestionsTab.css";
import ModalGlobal from "../../../../../../components/AppModal/ModalGlobal";
import { SurveyQuestion } from "../../../types";
import { deleteQuestion } from "../../../api/questionAPI";
import { useAlert } from "../../../../../../hooks/useAlert";
import AlertMessage from "../../../../../../components/AlertMessage";
import { useModal } from "../../../../../../hooks/useModal";
import { ApiErrorResponse } from "../../../../../../types/ApiErrorResponse";
import { UseSelectBasicObject } from "../../../../../../types/UseSelectBasicObject";
import SurveyQuestionsListDroppable from "./SurveyQuestionsListDroppable";
import SurveyPreview from "./SurveyQuestionsPreview";

export default function SurveyQuestionsTab({ onPreviewChange }: { onPreviewChange: (value: boolean) => void }) {
    const { t } = useTranslation(['question', 'common']);
    const dispatch = useDispatch();
    const questions = useSelector((state: RootState) => state.surveyForm.data.questions ?? []);

    const [selectedIndex, setIndex] = useState<number>(0)

    const { showModal, modalData, errorModal, endDate, openModal: openDeleteWithoutImputModal, closeModal, isLoading, currentItem } = useModal<SurveyQuestion>();
    const { showModal: showCopyModal, modalData: copyModalData, openModal: openCopyModal, closeModal: closeCopyModal, isLoading: isCopyLoading, currentItem: copyItem } = useModal<SurveyQuestion>();

    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });

    const [isPreview, setIsPreview] = useState(false); 
    const openPreview = () => { setIsPreview(true); onPreviewChange(true); };
    const closePreview = () => { setIsPreview(false); onPreviewChange(false); };

    const handleCopy = (question: SurveyQuestion, index: number) => {
        openCopyModal(
            { ...question, index },
            t("question:modal.modalCopyQuestionTitle"),
            t("question:modal.modalCopyQuestionSubTitle"),
            "deleteWithoutInput",
            ""
        );
        setIndex(questions.length); 
    };

    const onConfirmCopy = () => {
        dispatch(copyQuestion(copyItem!.index));
        closeCopyModal();
    };

    const handleDelete = async (question: SurveyQuestion, index: number) => {
        const questionWithIndex = { ...question, index };
        openDeleteWithoutImputModal(
            questionWithIndex,
            t("question:modal.modalDeleteQuestionTitle"),
            t("question:modal.modalDeleteQuestionSubTitle", { name: question.order }),
            "deleteWithoutInput",
            ""
        );
    };

    const onConfirmDelete = async () => {
        if (modalData.type === 'deleteWithoutInput') {
            dispatch(removeQuestion(currentItem!.index))
            closeModal();
            if (currentItem!.id != 0)
                await deleteQuestion(currentItem!.id)
                    .then(onDeleteQuestionSuccessfully)
                    .catch((error: ApiErrorResponse) => {
                        const errorMessage = (error?.message ?? t("question:modal.modalErrorDeleteMessage"));
                        showAlert({
                            message: errorMessage,
                            type: 'danger',
                            duration: 8000
                        });
                    });
        }
    }

    const onDeleteQuestionSuccessfully = async () => {
        setTimeout(() => {
            showAlert({
                message: t("question:modal.modalSuccesDeleteMessage"),
                type: 'success',
                duration: 8000
            });
        }, 100);
    }

    return (
        <div>
            <ModalGlobal<UseSelectBasicObject>
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                endDate={endDate}
                onSave={onConfirmDelete}
                isLoading={isLoading}
            />
            <ModalGlobal<UseSelectBasicObject>
                show={showCopyModal}
                onClose={closeCopyModal}
                title={copyModalData.title}
                contentType={copyModalData.type}
                subtitle={copyModalData.subtitle}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                onSave={onConfirmCopy}
                isLoading={isCopyLoading}
            />
            {isVisible && (
                <AlertMessage
                    message={alert!.message}
                    type={alert!.type}
                    onClose={hideAlert}
                />
            )}
            <Row className="mb-4">
                <Col md={3} className="survey-questions-list-container">    
                    <Row>
                        <label htmlFor="">{t("common:content")}</label>
                    </Row>
                    <SurveyQuestionsListDroppable setIndex={setIndex} handleDelete={handleDelete} handleCopy={handleCopy} />
                    <Row>
                        {!isPreview && (
                            <Button
                                variant="primary"
                                onClick={() => {
                                dispatch(addQuestion());
                                setIndex(questions.length);
                                }}
                            >
                                {t("surveyAdmin:addQuestion")}
                            </Button>
                        )}
                    </Row>
                </Col>
                <Col md={9}>
                {isPreview ? (
                    <SurveyPreview onClose={closePreview} currentIndex={selectedIndex} setCurrentIndex={setIndex} />
                    ) : questions[selectedIndex] ? (
                        <SurveyQuestionForm index={selectedIndex} onPreview={openPreview} />
                ) : (
                    <div>{t("surveyAdmin:selectOrCreateQuestion")}</div>
                )}
                </Col>
            </Row>
        </div>
    );
}


